import clsx from 'clsx';
import type { Quarter } from './periods';
import { getForecastContext } from './periods';
import { formatCurrency, formatPercent } from './CurrencyDisplay';
import { getYearColor } from './QuarterHeader';
import { useTableSettings } from './TableSettingsContext';

interface ComputedCellProps {
  value: number;
  quarter: Quarter;
  format?: 'currency' | 'percent' | 'number';
  className?: string;
  /** Why the cell is flagged by the tab audit - shown amber, with this as its tooltip. */
  flag?: string;
}

export function ComputedCell({
  value,
  quarter,
  format = 'currency',
  className,
  flag,
}: ComputedCellProps) {
  const { settings, isRecalculating } = useTableSettings();
  const fiscalYears = settings ? getForecastContext(settings).fiscalYears : [];
  const yearColor = getYearColor(quarter, fiscalYears);

  let display: string;
  if (format === 'percent') {
    display = value === 0 ? '-' : formatPercent(value);
  } else if (format === 'number') {
    display = value === 0 ? '-' : value.toLocaleString('en-US');
  } else {
    display = value === 0 ? '-' : formatCurrency(value);
  }

  return (
    <td
      className={clsx(
        'px-3 py-1.5 text-right text-sm tabular-nums border-r border-b border-gray-200 bg-gray-50',
        value === 0 ? 'text-gray-400' : value < 0 ? 'text-red-600' : 'text-gray-900',
        isRecalculating && 'opacity-50 transition-opacity duration-150',
        yearColor,
        className,
        flag && 'bg-amber-100 ring-1 ring-inset ring-amber-400',
      )}
      title={flag}
      data-flagged={flag ? 'true' : undefined}
    >
      {display}
    </td>
  );
}
